/*
 * Yutrients Formula PDP — client-side controller.
 *
 * Loaded by sections/yt-formula-pdp.liquid. The page itself is mostly
 * static Liquid; this script handles three behaviours:
 *
 *   1. PLAN RADIO SELECTION
 *      Toggles the .yt-formula-pdp__plan--selected class as the customer
 *      picks a plan card. We track which Recharge selling-plan ID to send
 *      with the cart-add call (null = one-time purchase).
 *
 *   2. CHECKOUT SECURELY NOW
 *      POST /cart/add.js with the variant + selected selling-plan, then
 *      redirect straight to /checkout. We skip the cart drawer entirely
 *      on this branch — the customer already made their plan decision on
 *      this page, so dropping them on /cart adds friction.
 *
 *   3. (No-op) all the "Edit"/"Add more" links are plain <a> tags pointed
 *      at /pages/build-your-own?stack=<formula_id>. The Liquid builds
 *      the URL once and shares it across every link, so there's nothing
 *      to wire up here.
 *
 * Selling plan IDs are read from data-* attributes on the section root,
 * populated from theme settings. For now Recharge isn't configured —
 * those attributes will be empty strings, the controller falls back to
 * a one-time purchase, and a single console.warn fires so we notice.
 */

(function () {
  'use strict';

  const SECTION_SELECTOR = '[data-yt-formula-pdp]';

  class FormulaPDPController {
    constructor(root) {
      this.root = root;

      // Cache the bits we touch repeatedly.
      this.variantId = root.getAttribute('data-yt-variant-id') || '';
      this.ingredientsText = (root.getAttribute('data-yt-ingredients-text') || '').slice(0, 500);
      this.sellingPlan1mo = root.getAttribute('data-yt-selling-plan-1mo') || '';
      this.sellingPlan3mo = root.getAttribute('data-yt-selling-plan-3mo') || '';
      // 2026-08-12: the 90-day offer is a PREPAID TWIN variant on the
      // dedicated prepaid product — selecting 3mo swaps WHICH variant we
      // add (qty stays 1; the twin's price is the full 3-month charge).
      this.prepaidVariantId = (root.getAttribute('data-yt-prepaid-variant-id') || '').replace(/\D/g, '');

      this.planEls = Array.from(root.querySelectorAll('[data-yt-plan]'));
      this.plansWrap = root.querySelector('[data-yt-offers]');
      this.onetimeReveal = root.querySelector('[data-yt-onetime-link]');
      this.checkoutBtn = root.querySelector('[data-yt-formula-checkout]');
      this.addBasketBtn = root.querySelector('[data-yt-formula-addbasket]');
      this.errorRegion = root.querySelector('[data-yt-formula-error]');

      // Sticky bar (2026-08-12 redesign): offer pill + drop-up + ADD.
      this.stickyBar = root.querySelector('[data-yt-sticky-bar]');
      this.stickyBasketBtn = root.querySelector('[data-yt-sticky-addbasket]');
      this.stickyPill = root.querySelector('[data-yt-sticky-pill]');
      this.stickyMenu = root.querySelector('[data-yt-sticky-menu]');
      this.stickyTitleEl = root.querySelector('[data-yt-sticky-title]');
      this.stickySubEl = root.querySelector('[data-yt-sticky-sub]');

      // Selected plan key — '3mo' | '1mo' | 'onetime'. Hydrated from
      // whichever radio is checked in the Liquid (Monthly by default).
      this.selectedPlanKey = this.initialSelectedKey();
      // 21 Oct 2026 (Lucas): the one-time 30-day test — the only offer is a
      // one-time tin at the subscription price; the line is stamped
      // `_yt_offer: ot30` for the Variant Manager discount Function.
      this.otMode = (root.getAttribute('data-yt-offer-mode') || window.__ytOfferMode) === 'one_time_30d';
      if (this.otMode) this.selectedPlanKey = 'onetime';

      this.bind();
      this.warnIfMissingPlans();
      this.hydrateIngredientThumbs();
    }

    initialSelectedKey() {
      const checked = this.root.querySelector('.yt-formula-pdp__offer-radio:checked');
      if (checked) return checked.value;
      // Defensive fallback: pick the .yt-formula-pdp__plan--selected card.
      const selectedCard = this.root.querySelector('.yt-formula-pdp__offer--selected');
      if (selectedCard) return selectedCard.getAttribute('data-yt-plan');
      return '1mo'; // 30-day default (founder 2026-09-01)
    }

    bind() {
      // Plan radios — listen on each label container so the whole card is
      // the tap target. The native <input type=radio> inside the label
      // fires `change` anyway; we update the visual --selected class.
      this.planEls.forEach((el) => {
        el.addEventListener('click', () => {
          const key = el.getAttribute('data-yt-plan');
          this.selectPlan(key);
        });
        const radio = el.querySelector('.yt-formula-pdp__offer-radio');
        if (radio) {
          radio.addEventListener('change', () => {
            if (radio.checked) this.selectPlan(radio.value);
          });
        }
      });

      if (this.checkoutBtn) {
        this.checkoutBtn.addEventListener('click', () => this.handleCheckout());
      }

      // Sticky bar (2026-08-12): the pill toggles a drop-up of the two
      // subscription offers; picking one syncs the main picker. ADD uses
      // the same handler + busy state as the page CTA.
      if (this.stickyBasketBtn) {
        this.stickyBasketBtn.addEventListener('click', () => this.handleAddToBasket());
      }
      if (this.stickyPill && this.stickyMenu) {
        this.stickyPill.addEventListener('click', () => {
          const open = this.stickyMenu.hidden;
          this.stickyMenu.hidden = !open;
          this.stickyPill.setAttribute('aria-expanded', String(open));
        });
        this.stickyMenu.querySelectorAll('[data-yt-sticky-option]').forEach((btn) => {
          btn.addEventListener('click', () => {
            this.selectPlan(btn.getAttribute('data-yt-sticky-option'));
            this.stickyMenu.hidden = true;
            this.stickyPill.setAttribute('aria-expanded', 'false');
          });
        });
        document.addEventListener('click', (e) => {
          if (!this.stickyBar.contains(e.target) && !this.stickyMenu.hidden) {
            this.stickyMenu.hidden = true;
            this.stickyPill.setAttribute('aria-expanded', 'false');
          }
        });
      }
      this.updateStickyOffer();
      this.updateBreakdown();
      this.hydrateStackSubs();

      // Founder 2026-07-12: the small "Add to basket" button adds the same
      // line with the same selected plan, then lands on the cart (not
      // /checkout) so the customer can keep shopping.
      if (this.addBasketBtn) {
        this.addBasketBtn.addEventListener('click', () => this.handleAddToBasket());
      }

      // Founder 2026-07-13: clicking a CTA disables it (setBusy) then sends
      // the customer to /checkout or /cart. When they hit BACK, the browser
      // restores this page from its bfcache with the button STILL disabled —
      // so it looks frozen until a manual refresh. Re-enable on every
      // pageshow (a no-op on first load; the fix on a bfcache restore).
      window.addEventListener('pageshow', () => this.setBusy(false));

      // Iter-2: underlined "One time purchase" link below the two
      // default plan bubbles. Clicking it reveals the hidden one-time
      // bubble (CSS handles the hide-link / show-bubble swap via the
      // .is-revealed class on the wrapper). The link is one-shot —
      // once revealed, the bubble stays for the session.
      if (this.onetimeReveal && this.plansWrap) {
        this.onetimeReveal.addEventListener('click', () => {
          this.plansWrap.classList.add('is-revealed');
          this.selectPlan('onetime');
        });
      }
    }

    selectPlan(key) {
      this.selectedPlanKey = key;
      this.planEls.forEach((el) => {
        const match = el.getAttribute('data-yt-plan') === key;
        el.classList.toggle('yt-formula-pdp__offer--selected', match);
        const radio = el.querySelector('.yt-formula-pdp__offer-radio');
        if (radio) radio.checked = match;
      });
      this.updateStickyOffer();
      this.updateBreakdown();
      this.hydrateStackSubs();
    }

    /**
     * Ingredient sub-lines match the build-your-own cards (founder
     * 2026-08-14): the catalogue TAGLINE by slug replaces the AI
     * rationale, so both pages describe ingredients identically.
     * Fetched once from the public catalogue; on failure the existing
     * subs stay.
     */
    async hydrateStackSubs() {
      const rows = Array.from(this.root.querySelectorAll('.yt-formula-pdp__stack-row'));
      if (!rows.length) return;
      try {
        const res = await fetch('/apps/yutrients/catalogue', { headers: { Accept: 'application/json' } });
        if (!res.ok) return;
        const cat = await res.json();
        const bySlug = new Map((cat.ingredients || []).map((i) => [i.slug, i.tagline || '']));
        rows.forEach((row) => {
          const slugEl = row.querySelector('[data-yt-ing-slug]');
          const body = row.querySelector('.yt-formula-pdp__stack-body');
          if (!slugEl || !body) return;
          const tagline = bySlug.get(slugEl.getAttribute('data-yt-ing-slug'));
          if (!tagline) return;
          let sub = body.querySelector('.yt-formula-pdp__stack-sub');
          if (!sub) {
            sub = document.createElement('span');
            sub.className = 'yt-formula-pdp__stack-sub';
            body.appendChild(sub);
          }
          sub.textContent = tagline;
        });
      } catch (_e) { /* keep whatever subs rendered server-side */ }
    }

    /**
     * YOUR BLEND price breakdown (founder 2026-08-14): ingredient-cost
     * lines + the Lab blending fee + total, all at the SELECTED offer's
     * rate. Fees are the dispensing charge per offer — LOCKSTEP with
     * formulator pricing.ts DISPENSING_POST_MARGIN_GBP (100/7 at the
     * 30-day rate): 90-day £12.50, 30-day £14.29, one-time £17.86.
     * Ingredient lines rescale the snapshotted shares proportionally to
     * (offer price − fee), so old-format snapshots render correctly too.
     */
    updateBreakdown() {
      const bd = this.root.querySelector('[data-yt-breakdown]');
      if (!bd) return;
      const key = this.selectedPlanKey || '1mo';
      const cents = Number(bd.getAttribute(
        key === '3mo' ? 'data-p3' : key === '1mo' ? 'data-p1' : 'data-pot',
      )) || 0;
      const price = cents / 100;
      const FEES = { '3mo': 12.5, '1mo': 100 / 7, onetime: 125 / 7 };
      const fee = FEES[key] || FEES['3mo'];
      const unit = key === 'onetime' ? '' : '/mo';
      const feeEl = bd.querySelector('[data-yt-blend-fee]');
      const totalEl = bd.querySelector('[data-yt-breakdown-total]');
      // Ledger alignment, rev 2 (founder 2026-08-14): amounts are COMPACT
      // ("£9.85", no internal gap) and left-pinned inside fixed-width
      // boxes, so the £ signs still share one column. The total's unit is
      // zero-width so "/mo" trails right without growing the box.
      if (feeEl) feeEl.textContent = '£' + fee.toFixed(2);
      if (totalEl) totalEl.innerHTML = '&pound;' + price.toFixed(2)
        + (unit ? '<span class="yt-formula-pdp__total-unit">' + unit + '</span>' : '');

      const spans = Array.from(this.root.querySelectorAll('[data-yt-share]'));
      if (!spans.length || !price) return;
      const base = spans.map((sp) => Number(sp.getAttribute('data-yt-share')) || 0);
      const sum = base.reduce((a, b) => a + b, 0);
      const pool = Math.max(0, Math.round((price - fee) * 100) / 100);
      if (sum <= 0) return;
      let acc = 0;
      spans.forEach((sp, i) => {
        let v;
        if (i === spans.length - 1) {
          v = Math.round((pool - acc) * 100) / 100;
        } else {
          v = Math.round((base[i] / sum) * pool * 100) / 100;
        }
        acc = Math.round((acc + v) * 100) / 100;
        sp.textContent = '£' + v.toFixed(2);
      });
    }

    /**
     * Sticky pill mirrors the selected offer: title + save % + the
     * per-serving price, all read from the offer cards' rendered DOM so
     * the bar can never disagree with the picker. One-time selections
     * show the one-time price (the pill lists only the two subscription
     * offers, but the ADD button always honours the page selection).
     */
    updateStickyOffer() {
      if (!this.stickyBar || !this.stickyTitleEl) return;
      const key = this.selectedPlanKey;
      const card = this.root.querySelector('[data-yt-plan="' + key + '"]');
      if (!card) return;
      if (key === 'onetime') {
        const price = card.querySelector('.yt-formula-pdp__offer-price--onetime');
        this.stickyTitleEl.textContent = 'One time purchase';
        this.stickySubEl.textContent = price ? price.textContent.trim() : '';
        return;
      }
      const title = card.querySelector('.yt-formula-pdp__offer-title');
      const save = card.querySelector('.yt-formula-pdp__offer-save');
      const serving = card.querySelector('.yt-formula-pdp__offer-serving');
      const saveText = save ? ' (' + save.textContent.trim().replace('SAVE', 'Save') + ')' : '';
      this.stickyTitleEl.textContent = (title ? title.textContent.trim() : '') + saveText;
      this.stickySubEl.textContent = serving
        ? serving.textContent.trim().replace('SERVING', 'serving')
        : '';
    }

    /**
     * Map the selected plan key to the Recharge selling-plan ID to send
     * with the cart-add POST. Returns null for one-time purchase OR when
     * the corresponding ID isn't configured yet — Shopify treats a null
     * selling_plan as a plain one-off purchase.
     */
    resolveSellingPlanId() {
      if (this.selectedPlanKey === '1mo') return this.sellingPlan1mo || null;
      if (this.selectedPlanKey === '3mo' && this.prepaidVariantId) return this.sellingPlan3mo || null;
      return null;
    }

    warnIfMissingPlans() {
      if (!this.sellingPlan1mo) {
        console.warn(
          '[yt-formula-pdp] No Recharge selling-plan ID for Monthly Delivery — checkout will fall back to a one-time purchase. Set the ID in the section settings.'
        );
      }
      if (!this.sellingPlan3mo) {
        console.warn(
          '[yt-formula-pdp] No Recharge selling-plan ID for Every 3 Months — checkout will fall back to a one-time purchase. Set the ID in the section settings.'
        );
      }
    }

    async handleCheckout() {
      if (!this.variantId) {
        this.showError('Missing variant — try refreshing the page.');
        return;
      }
      if (this.checkoutBtn.disabled) return;

      this.hideError();
      this.setBusy(true);

      try {
        await this.addToCart();
        // Skip the cart page — the customer already chose their plan
        // here, so /checkout is the right next stop.
        window.location.assign('/checkout');
      } catch (err) {
        this.setBusy(false);
        const msg = (err && err.message) || 'Could not start checkout — please try again.';
        this.showError(msg);
      }
    }

    async handleAddToBasket() {
      if (!this.variantId) {
        this.showError('Missing variant — try refreshing the page.');
        return;
      }
      if (this.addBasketBtn && this.addBasketBtn.disabled) return;

      this.hideError();
      this.setBusy(true);

      try {
        await this.addToCart();
        // 2026-08-12: open the cart drawer in place (IM8-patterned)
        // instead of navigating away; fall back to /cart if the drawer
        // isn't on the page.
        this.setBusy(false);
        if (window.__ytCartDrawer) {
          await window.__ytCartDrawer.open();
        } else {
          window.location.assign('/cart');
        }
      } catch (err) {
        this.setBusy(false);
        const msg = (err && err.message) || 'Could not add to basket — please try again.';
        this.showError(msg);
      }
    }

    /**
     * Reconcile the cart to contain exactly ONE line for this formulation,
     * matching the plan + quantity the customer is currently looking at,
     * then return. Both CTAs call this.
     */
    async addToCart() {
      const primaryId = String(this.variantId).replace(/\D/g, '');
      // 2026-08-12: the 90-day offer adds the PREPAID TWIN variant (its
      // price IS the full 3-month charge) with the prepaid selling plan.
      // 30-day + one-time add the primary. Quantity is always 1.
      const useTwin = this.selectedPlanKey === '3mo' && this.prepaidVariantId;
      const numericVariantId = useTwin ? this.prepaidVariantId : primaryId;
      const otherVariantId = useTwin ? primaryId : this.prepaidVariantId;
      const sellingPlanId = this.resolveSellingPlanId();
      return this.ensureLineInCart(numericVariantId, sellingPlanId, 1, otherVariantId);
    }

    /**
     * IDEMPOTENT ADD (founder 2026-07-13).
     *
     * This page owns exactly ONE cart line for this formulation. Every
     * Checkout / Add-to-basket click reconciles that line to the plan +
     * quantity the customer is currently looking at, instead of blindly
     * POSTing /cart/add.js.
     *
     * Why: the customer clicks Checkout (line added → we redirect to
     * /checkout), then hits BACK. The browser restores this page from
     * bfcache and they click Checkout again — a naive add would put a
     * SECOND identical formulation in the cart (the founder's report:
     * "two products in the checkout not one"). Keying on the variant id
     * (unique per formulation) makes the repeat click a no-op — or a plan
     * update if they changed their mind on this visit — and cleans up any
     * duplicate line the old behaviour already created.
     */
    async ensureLineInCart(numericVariantId, sellingPlanId, quantity, otherVariantId) {
      const desiredPlan = sellingPlanId ? String(sellingPlanId) : null;

      let cart = null;
      try {
        const r = await fetch('/cart.js', { headers: { Accept: 'application/json' } });
        if (r.ok) cart = await r.json();
      } catch (_e) {
        // Cart read failed — fall through to a plain add below.
      }

      // The primary and its prepaid twin are the SAME formulation on two
      // products — never both in the cart. Drop the sibling's lines first
      // so switching offers replaces rather than accumulates.
      if (cart && Array.isArray(cart.items) && otherVariantId) {
        const siblings = cart.items.filter((it) => String(it.variant_id) === String(otherVariantId));
        for (const line of siblings) {
          await this.changeLine({ id: line.key, quantity: 0 });
        }
      }

      const matches =
        cart && Array.isArray(cart.items)
          ? cart.items.filter((it) => String(it.variant_id) === numericVariantId)
          : [];

      // Not in the cart yet → normal add (carries the 422 retry for a
      // brand-new variant that hasn't hit the storefront cart index yet).
      if (!matches.length) {
        return this.addLineToCart(numericVariantId, sellingPlanId, quantity);
      }

      // Already present. Drop any extra duplicate lines left over from the
      // earlier double-add so we converge on a single line.
      for (let i = 1; i < matches.length; i++) {
        await this.changeLine({ id: matches[i].key, quantity: 0 });
      }

      const primary = matches[0];
      const currentPlan =
        (primary.selling_plan_allocation &&
          primary.selling_plan_allocation.selling_plan &&
          String(primary.selling_plan_allocation.selling_plan.id)) ||
        null;

      // Surviving line already matches the current selection → no-op.
      if (matches.length === 1 && currentPlan === desiredPlan && primary.quantity === quantity) {
        return primary;
      }

      // Otherwise update the surviving line in place to the current plan +
      // quantity. selling_plan: null clears the plan (→ one-time).
      return this.changeLine({ id: primary.key, quantity: quantity, selling_plan: desiredPlan });
    }

    /** POST /cart/change.js for a single line; returns the cart or throws. */
    async changeLine(patch) {
      const res = await fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(patch),
      });
      if (!res.ok) {
        await res.text().catch(() => ''); // drain; never surface the raw body
        throw new Error('Could not update your basket — please try again.');
      }
      return res.json();
    }

    /**
     * POST /cart/add.js with the variant + (optional) Recharge
     * selling-plan ID. Returns the added line on success; on failure throws
     * an Error carrying CUSTOMER-SAFE copy (never Shopify's raw body). Only
     * called when the formulation isn't already in the cart.
     *
     * Retry on 422 (ported from yt-byo.js addVariantToShopifyCart):
     *
     * Newly-created variants take ~5-60s to appear in Shopify's
     * storefront cart index after productVariantsBulkCreate, even
     * though publishablePublish runs on the server. The variant shows
     * up in /products/<handle>.js immediately, but /cart/add.js returns
     * 422 with "already sold out" (Shopify's misleading default message
     * for "variant exists but isn't in the cart-eligible index yet")
     * until the index catches up. A customer who reaches this Formula
     * PDP right after building a stack hits exactly that window.
     *
     * Retry across a ~22s budget (6 attempts, backoff 1.5/3/4/6/8s) — long
     * enough to clear almost every observed lag, short enough not to strand
     * the customer. We don't retry non-422 errors (those are genuine), and
     * on exhaustion we surface a friendly "still being prepared" message so
     * the customer never sees the raw "already sold out" text.
     */
    async addLineToCart(numericVariantId, sellingPlanId, quantity) {
      const item = { id: numericVariantId, quantity: quantity };
      // Per-order ingredient list as a line-item property → rides onto the
      // Shopify order so Klaviyo's native order sync can show the actual blend.
      if (this.ingredientsText) item.properties = { Ingredients: this.ingredientsText };
      if (sellingPlanId) {
        item.selling_plan = sellingPlanId;
      } else if (this.otMode) {
        item.properties = Object.assign({}, item.properties || {}, { _yt_offer: 'ot30' });
      }
      // 2026-07 model: the tin is the ONLY line. The £14.95 weighing spoon is
      // baked into the one-time variant price (and free via the selling-plan
      // discount on subscriptions) — nothing else is auto-added to the cart.
      const body = { items: [item] };

      // ~22s budget across 6 attempts — long enough to clear almost every
      // observed index lag (the ~5-60s window above skews low), short enough
      // not to strand the customer on a spinner. The 422 "already sold out"
      // is the lag signal, NOT a real stock-out (these variants are
      // made-to-order with inventory untracked), so we keep retrying it.
      const delays = [0, 1500, 3000, 4000, 6000, 8000];
      for (let i = 0; i < delays.length; i++) {
        if (delays[i] > 0) {
          await new Promise((resolve) => setTimeout(resolve, delays[i]));
        }
        // Cap each attempt so a hung request can't leave the CTA spinning
        // forever with no error and no recovery.
        const controller = new AbortController();
        const timer = setTimeout(() => controller.abort(), 12000);
        let res;
        try {
          res = await fetch('/cart/add.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(body),
            signal: controller.signal,
          });
        } catch (err) {
          if (err && err.name === 'AbortError') {
            throw new Error('That took too long — please try again in a moment.');
          }
          throw err;
        } finally {
          clearTimeout(timer);
        }
        if (res.ok) return res.json();
        // Non-422 → a genuine failure. NEVER surface Shopify's raw body: for a
        // made-to-order blend it can read "already sold out", which is both
        // wrong and alarming to the customer.
        if (res.status !== 422) {
          await res.text().catch(() => ''); // drain, but don't show it
          throw new Error('Something went wrong adding your blend — please try again.');
        }
        // 422 = the storefront cart index is still catching up; loop + retry.
      }
      // Budget exhausted while the index was still catching up. Friendly copy,
      // never the raw "already sold out" text — a retry moments later works.
      throw new Error('Your blend is still being prepared — please try again in a few seconds.');
    }

    /**
     * INGREDIENT THUMBNAILS (2026-07-09).
     *
     * Every catalogue ingredient is a Shopify product whose handle equals
     * the ingredient slug, so each blend row's circular thumb can show the
     * product's featured image. The Liquid stamps data-yt-ing-slug on each
     * thumb; this fetches /products/<slug>.js client-side and swaps the
     * image in. Design constraints:
     *
     *   - LAZY: the blend list sits below the buy box, so fetching starts
     *     only when the list is within ~400px of the viewport
     *     (IntersectionObserver; fetch-immediately fallback without it).
     *   - BATCHED: max 4 requests in flight; duplicate slugs share one
     *     fetch via the per-slug map.
     *   - FAIL-SOFT: a 404 (ingredient without a product, e.g. mct-oil),
     *     a missing image, or a network error just leaves the cream
     *     fallback circle — the <img> is only attached AFTER it has
     *     actually loaded, so a broken-image icon can never appear and
     *     the row's fixed 64px circle keeps the layout identical either
     *     way. (Many ingredient products currently share one holding
     *     image — expected.)
     */
    hydrateIngredientThumbs() {
      const thumbs = Array.from(this.root.querySelectorAll('[data-yt-ing-slug]'));
      if (!thumbs.length) return;

      let started = false;
      const start = () => {
        if (started) return;
        started = true;
        this.loadIngredientThumbs(thumbs);
      };

      const list = this.root.querySelector('.yt-formula-pdp__stack-list');
      if (typeof IntersectionObserver === 'function' && list) {
        const io = new IntersectionObserver(
          (entries) => {
            if (entries.some((e) => e.isIntersecting)) {
              io.disconnect();
              start();
            }
          },
          { rootMargin: '400px 0px' }
        );
        io.observe(list);
      } else {
        start();
      }
    }

    async loadIngredientThumbs(thumbs) {
      // One fetch per UNIQUE slug — a blend can't normally repeat an
      // ingredient, but a shared payload shouldn't cost two requests.
      // Carry the ingredient NAME alongside so we can fall back to a
      // product search when the slug's handle has drifted (renamed
      // ingredient / legacy variant) and /products/<slug>.js 404s.
      const bySlug = new Map();
      thumbs.forEach((el) => {
        const slug = (el.getAttribute('data-yt-ing-slug') || '').trim().toLowerCase();
        if (!slug) return;
        if (!bySlug.has(slug)) {
          bySlug.set(slug, { els: [], name: (el.getAttribute('data-yt-ing-name') || '').trim() });
        }
        bySlug.get(slug).els.push(el);
      });

      const queue = Array.from(bySlug.entries());
      const CONCURRENCY = 4;
      const worker = async () => {
        while (queue.length) {
          const next = queue.shift();
          if (!next) return;
          const [slug, entry] = next;
          const src = await this.fetchProductThumbSrc(slug, entry.name);
          if (src) entry.els.forEach((el) => this.applyThumb(el, src));
        }
      };
      const workers = [];
      for (let i = 0; i < Math.min(CONCURRENCY, queue.length); i++) workers.push(worker());
      await Promise.all(workers);
    }

    /**
     * Resolve an ingredient to a thumb-sized featured-image URL, or null.
     * Primary path: /products/<slug>.js (handle === slug by convention).
     * Fallback (2026-07-12): when that 404s — ingredient slugs can drift
     * from product handles after a rename (e.g. ashwagandha-ksm-66 →
     * ashwagandha) — search by name and take the featured image of the
     * best title match, so the thumbnail survives handle drift.
     */
    async fetchProductThumbSrc(slug, name) {
      const bySlug = await this.fetchProductImageByHandle(slug);
      if (bySlug) return this.cdnThumb(bySlug);
      const byName = await this.fetchProductImageByName(name);
      if (byName) return this.cdnThumb(byName);
      return null;
    }

    /** /products/<handle>.js → featured image string, or null on 404/miss. */
    async fetchProductImageByHandle(handle) {
      if (!handle) return null;
      try {
        const res = await fetch('/products/' + encodeURIComponent(handle) + '.js', {
          headers: { Accept: 'application/json' },
        });
        if (!res.ok) return null; // 404 = no product for this handle
        const product = await res.json();
        return this.pickProductImage(product);
      } catch (_e) {
        return null; // network blip — keep the fallback circle
      }
    }

    /**
     * Search suggest by ingredient name → best title match's image, or null.
     *
     * Shopify's suggest tokeniser won't match the metafield's form-specific
     * name verbatim ("Ashwagandha (KSM-66)", "Zinc bisglycinate") against the
     * cleaned product title, so we try progressively simpler queries: the
     * full name, then with parentheticals stripped, then just the first word
     * — the first word reliably matches (verified live: "Ashwagandha" →
     * "Ashwagandha", "Zinc" → "Zinc (Bisglycinate)", "Rhodiola" → "Rhodiola
     * Rosea"). First query that yields a confident match wins.
     */
    async fetchProductImageByName(name) {
      const raw = (name || '').trim();
      if (!raw) return null;

      const noParens = raw.replace(/\([^)]*\)/g, ' ').replace(/\s+/g, ' ').trim();
      const firstWord = (noParens || raw).split(/[\s/]+/)[0];
      const wantWord = firstWord.toLowerCase();
      const lc = (s) => (s || '').trim().toLowerCase();
      const firstTok = (s) => lc(s).split(/[\s(]/)[0];

      const queries = [];
      [raw, noParens, firstWord].forEach((q) => {
        if (q && queries.indexOf(q) === -1) queries.push(q);
      });

      for (let i = 0; i < queries.length; i++) {
        try {
          const url =
            '/search/suggest.json?q=' +
            encodeURIComponent(queries[i]) +
            '&resources[type]=product&resources[limit]=6&resources[options][unavailable_products]=show';
          const res = await fetch(url, { headers: { Accept: 'application/json' } });
          if (!res.ok) continue;
          const data = await res.json();
          const products =
            (data && data.resources && data.resources.results && data.resources.results.products) ||
            [];
          if (!products.length) continue;
          // exact full name → exact first word → same-first-word → top hit.
          const match =
            products.find((p) => lc(p.title) === lc(raw)) ||
            products.find((p) => lc(p.title) === wantWord) ||
            products.find((p) => firstTok(p.title) === wantWord) ||
            products[0];
          const img = match && (match.featured_image || match.image);
          // suggest.json shapes featured_image as an object OR a string.
          if (img && typeof img === 'object') {
            const s = img.url || img.src;
            if (s) return s;
          } else if (typeof img === 'string') {
            return img;
          }
        } catch (_e) {
          // try the next, simpler query
        }
      }
      return null;
    }

    /** Pull a featured image string off a /products/<handle>.js payload. */
    pickProductImage(product) {
      const img =
        product &&
        (product.featured_image || (Array.isArray(product.images) && product.images[0]));
      return typeof img === 'string' ? img : null;
    }

    /** Ask the Shopify CDN for a 128px rendition (circle renders at 64px). */
    cdnThumb(img) {
      try {
        const u = new URL(img, window.location.origin);
        u.searchParams.set('width', '128');
        return u.toString();
      } catch (_e) {
        return img;
      }
    }

    /** Attach the product photo only once it has LOADED (fail-soft swap). */
    applyThumb(el, src) {
      const img = document.createElement('img');
      img.className = 'yt-formula-pdp__stack-thumb-photo';
      img.alt = ''; // decorative — the row already carries the ingredient name
      img.width = 64;
      img.height = 64;
      img.decoding = 'async';
      img.addEventListener('load', () => {
        el.classList.add('is-loaded');
        el.appendChild(img);
      });
      // No error handler needed: a failed load simply never attaches.
      img.src = src;
    }

    setBusy(isBusy) {
      // Disable ALL CTAs (buy box + sticky bar) while a cart write is in
      // flight so a customer can't fire two adds at the same time.
      [
        this.checkoutBtn,
        this.addBasketBtn,
        this.stickyCheckoutBtn,
        this.stickyBasketBtn,
      ].forEach((btn) => {
        if (!btn) return;
        btn.disabled = isBusy;
        btn.setAttribute('aria-busy', isBusy ? 'true' : 'false');
      });
    }

    showError(message) {
      if (!this.errorRegion) return;
      this.errorRegion.textContent = message;
      this.errorRegion.hidden = false;
    }
    hideError() {
      if (!this.errorRegion) return;
      this.errorRegion.hidden = true;
      this.errorRegion.textContent = '';
    }
  }

  function init() {
    const root = document.querySelector(SECTION_SELECTOR);
    if (!root) return;
    new FormulaPDPController(root);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
